/**
 * Which blending sessions a blending session already contains (D-BL9).
 *
 * A component may name another blend, so the sessions form a graph, and the one
 * shape that graph must never take is a cycle: a blend inside itself has no
 * volume, no gravity and no end to the walk that would compute them.
 *
 * Its own module rather than a function in `blend.ts` because the walk reads
 * the workspace's whole `blends` table, and `workspace.ts` is built out of
 * `blend.ts` — the import would run the other way. Both the `blend.update`
 * guard and the page's picker call this, so the rule the document enforces and
 * the rule the picker shows are the same code.
 */

import type { WorkspaceState } from './workspace';

/**
 * True when `outerId` is `innerId`, or when following `blendId`s down from
 * `innerId` reaches `outerId`.
 *
 * Read it as the question the picker asks: "would putting this blend inside
 * that one close a loop?" Ids that name no stored blend are dead ends, since a
 * deleted source is provenance and is never dereferenced.
 */
export function blendContains(
  state: Pick<WorkspaceState, 'blends'>,
  outerId: string,
  innerId: string,
): boolean {
  if (outerId === innerId) return true;
  const seen = new Set<string>();
  const pending = [innerId];
  while (pending.length > 0) {
    const id = pending.pop();
    if (id === undefined || seen.has(id)) continue;
    seen.add(id);
    const blend = state.blends[id];
    if (blend === undefined) continue;
    for (const component of blend.components) {
      if (component.blendId === null) continue;
      if (component.blendId === outerId) return true;
      pending.push(component.blendId);
    }
  }
  return false;
}
